import * as Cookies from 'universal-cookie';

interface GenerateDashboardUrlRequest {
    watrRequestId: number;
    destinationPath?: string;
}

interface GenerateDashboardUrlResponse {
    success: boolean;
    message: string;
    errorCode?: string;
    data?: {
        dashboardUrl: string;
        oneTimeToken: string;
    };
}

export const ADMIN_CONTEXT_REQUIRED = 'ADMIN_CONTEXT_REQUIRED';

export const ADMIN_CONTEXT_REQUIRED_MESSAGE =
    'Unable to open DonorShield: admin sign-in information is missing. Sign in again.';

export const DONOR_SHIELD_OPEN_ERROR_USER_MESSAGE =
    'Unable to open DonorShield. Please try again or contact support.';

export const logDonorShieldOpenFailure = (context: string, detail: unknown): void => {
    console.log(`[DonorShield open] ${context}`, detail);
};

export const getAdminAuthContext = (): { username: string; userId: number } => {
    try {
        const cookies = new Cookies();
        const nkrAuth = cookies.get('NkrAuth');
        const user = nkrAuth?.user;
        const username = (user?.username || user?.userName || '').trim();
        const userId = user?.id > 0 ? user.id : 0;
        return { username, userId };
    } catch {
        return { username: '', userId: 0 };
    }
};

export const hasValidAdminAuthContext = (): boolean => {
    const { username, userId } = getAdminAuthContext();
    return username.length > 0 && userId > 0;
};

export const donorShieldUrlHasAdminContext = (dashboardUrl: string): boolean => {
    try {
        const url = new URL(dashboardUrl);
        const adminUser = (url.searchParams.get('adminUser') || '').trim();
        const adminUserId = parseInt(url.searchParams.get('adminUserId') || '0', 10);
        return adminUser.length > 0 && !Number.isNaN(adminUserId) && adminUserId > 0;
    } catch {
        return false;
    }
};

const getAuthToken = (): string | null => {
    try {
        const cookies = new Cookies();
        const nkrAuth = cookies.get('NkrAuth');
        if (nkrAuth && nkrAuth.token && nkrAuth.token.access_token) {
            return nkrAuth.token.access_token;
        }
    } catch (error) {
        console.error('Error getting auth token:', error);
    }
    return null;
};

export const appendAdminContextToDonorShieldUrl = (dashboardUrl: string): string => {
    const { username, userId } = getAdminAuthContext();
    if (!username || userId <= 0) {
        throw new Error(ADMIN_CONTEXT_REQUIRED);
    }
    const url = new URL(dashboardUrl);
    url.searchParams.set('adminUser', username);
    url.searchParams.set('adminUserId', String(userId));
    return url.toString();
};

export const generateDashboardUrl = async (watrRequestId: number, destinationPath: string = 'dashboard'): Promise<GenerateDashboardUrlResponse> => {
    const request: GenerateDashboardUrlRequest = {
        watrRequestId,
        destinationPath
    };

    const token = getAuthToken();
    const headers: HeadersInit = {
        'Content-Type': 'application/json'
    };

    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }

    try {
        const response = await fetch(`${__API__}/dashboard-access/generate-url`, {
            method: 'POST',
            headers,
            credentials: 'include',
            body: JSON.stringify(request)
        });

        const data = await response.json().catch(() => ({
            success: false,
            message: 'Failed to generate dashboard URL'
        })) as GenerateDashboardUrlResponse;

        if (!response.ok) {
            return {
                success: false,
                message: data.message || `HTTP error! status: ${response.status}`,
                errorCode: data.errorCode
            };
        }

        return data;
    } catch (error) {
        return {
            success: false,
            message: error instanceof Error ? error.message : 'Failed to generate dashboard URL'
        };
    }
};
